import React from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Landmark, FileText, Car, Zap, User, Check, X, Lock } from "lucide-react";
import BusinessEngineDiagram from "@/components/BusinessEngineDiagram";

const CARDS = [
  { icon: Clock, q: "¿Y SI ESTÁS PERDIENDO EL TIEMPO?", a: "Con Autofinanza, al minuto obtienes las alternativas que mejor se adaptan a tu perfil." },
  { icon: Landmark, q: "¿Y SI ESTÁS VIENDO LA OPCIÓN EQUIVOCADA?", a: "Con Autofinanza, comparamos múltiples instituciones al mismo tiempo." },
  { icon: FileText, q: "¿Y SI ENTREGAS DOCUMENTOS PARA UNA OPCIÓN QUE NI SIQUIERA ES LA MEJOR?", a: "Con Autofinanza, al minuto conoces tus alternativas, eliges la que más te conviene y solo entonces avanzas." },
  { icon: Car, q: "¿Y SI EXISTE UNA MEJOR FORMA DE ADQUIRIR TU VEHÍCULO?", a: "Con Autofinanza comparas crédito, arrendamiento y contado en una sola plataforma." },
  { icon: Zap, q: "¿Y SI ESTÁS ESPERANDO DÍAS PARA OBTENER RESPUESTAS?", a: "Con Autofinanza, al minuto obtienes resultados personalizados y comienzas tu proceso." },
  { icon: User, q: "¿Y SI TIENES QUE LLENAR EL MISMO FORMULARIO UNA Y OTRA VEZ?", a: "Con Autofinanza capturas tu información una sola vez. Nosotros hacemos el trabajo de comparación por ti." },
];

const COMPARISON = [
  ["Pierdes tiempo visitando bancos y agencias para comparar opciones.", "Al minuto conoces las alternativas que mejor se adaptan a tu perfil."],
  ["Llenas el mismo formulario una y otra vez.", "Capturas tu información una sola vez."],
  ["Solo conoces la opción que una institución quiere ofrecerte.", "Comparamos múltiples instituciones para ayudarte a encontrar la mejor alternativa."],
  ["Tomas decisiones con información limitada.", "Recibes una recomendación personalizada para decidir con confianza."],
  ["Entregas documentos sin saber si esa es la mejor opción.", "Primero conoces tus alternativas y después decides avanzar."],
  ["Esperas respuestas para saber si vale la pena continuar.", "Al minuto obtienes resultados personalizados y comienzas tu proceso."],
  ["Debes investigar por tu cuenta si te conviene crédito, arrendamiento o contado.", "Comparas las tres modalidades en una sola plataforma."],
  ["Si una opción no funciona, vuelves a empezar desde cero.", "Comienzas con la alternativa que mejor se adapta a tu perfil."],
];

export default function PorQueElegir() {
  const navigate = useNavigate();
  return (
    <section id="por-que" className="py-16 md:py-24" style={{ background: "var(--af-black)" }}>
      <div className="af-container">
        {/* Heading */}
        <div className="text-center max-w-4xl mx-auto">
          <div className="text-af-orange-core font-display uppercase tracking-[0.2em] text-sm mb-4">¿Por qué elegir Autofinanza?</div>
          <h2 className="font-round font-extrabold leading-tight" style={{ fontSize: "clamp(1.9rem,4.5vw,3.1rem)" }}>
            <span className="text-white">En Autofinanza transformamos el caos…</span><br />
            <span className="text-af-orange-core">…en la tranquilidad de elegir bien.</span>
          </h2>
        </div>

        {/* Business Engine diagram */}
        <BusinessEngineDiagram />

        {/* DEJA ATRÁS LA INCERTIDUMBRE */}
        <div className="text-center mt-20 mb-10">
          <h3 className="h-impact text-white text-2xl md:text-3xl">Deja atrás la incertidumbre</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {CARDS.map((c, i) => (
            <div key={i} className="text-center" data-testid={`incertidumbre-${i}`}>
              <c.icon size={40} className="mx-auto text-af-orange-core" strokeWidth={1.5} />
              <div className="text-white font-display font-semibold uppercase text-sm mt-4 leading-tight">{c.q}</div>
              <div className="text-white/55 text-xs mt-3 leading-relaxed">{c.a}</div>
            </div>
          ))}
        </div>

        {/* Comparison table */}
        <div className="text-center mt-20 mb-8">
          <h3 className="h-impact text-white text-2xl md:text-3xl">Proceso tradicional vs. <span className="text-orange-core">Autofinanza</span></h3>
        </div>
        <div className="af-card overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div className="p-4 md:p-5 flex items-center gap-2 font-display uppercase font-semibold text-white/90"
              style={{ borderBottom: "1px solid var(--af-border-dark)", borderRight: "1px solid var(--af-border-dark)" }}>
              <X size={22} className="text-red-500" /> Proceso tradicional
            </div>
            <div className="p-4 md:p-5 flex items-center gap-2 font-display uppercase font-semibold text-white/90"
              style={{ borderBottom: "1px solid var(--af-border-dark)" }}>
              <Check size={22} className="text-green-500" /> Con Autofinanza
            </div>
          </div>
          {COMPARISON.map(([bad, good], i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-2 cmp-row">
              <div className="p-4 md:p-5 flex items-start gap-3 md:border-r border-af-border-dark">
                <X size={18} className="text-red-500 shrink-0 mt-0.5" />
                <span className="text-white/70 text-sm">{bad}</span>
              </div>
              <div className="p-4 md:p-5 flex items-start gap-3 border-t md:border-t-0 border-af-border-dark">
                <Check size={18} className="text-green-500 shrink-0 mt-0.5" />
                <span className="text-white/85 text-sm">{good}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA banner */}
        <div className="af-card mt-12 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <div className="rounded-full flex items-center justify-center shrink-0" style={{ width: 64, height: 64, background: "rgba(232,93,4,0.15)", border: "1px solid rgba(232,93,4,0.4)" }}>
              <Zap size={30} className="text-af-orange-core" />
            </div>
            <div className="font-round font-bold text-2xl md:text-3xl leading-tight">
              <span className="text-white">Deja de comprar por prueba y error.</span><br />
              <span className="text-af-orange-core">Empieza a decidir con información.</span>
            </div>
          </div>
          <div className="flex flex-col items-center md:items-end gap-2 shrink-0">
            <button data-testid="porque-cta" onClick={() => navigate("/registro")} className="af-btn-primary">
              Iniciar precalificación gratuita <span aria-hidden>→</span>
            </button>
            <div className="flex items-center gap-2 text-white/60 text-sm"><Lock size={14} className="text-af-orange-core" /> 100% en línea, seguro y sin compromiso.</div>
          </div>
        </div>
      </div>
    </section>
  );
}
